/*
 * Cart drawer (static section "cart-drawer").
 * Dawn v16 contract: product-form.js calls setActiveElement(), getSectionsToRender() and renderContents().
 * CartDrawerItems (cart.js CartItems subclass) re-renders .drawer__inner through the Section Rendering API.
 * Bioroot additions (Shrine parity): countdown timer kept running across re-renders, animated progress bars,
 * re-render on external cart changes, <cart-drawer-upsell> variant pickers and <cart-discount> codes.
 */

const CART_DRAWER_SECTION = 'cart-drawer';

/* Copy live state from the current drawer into freshly parsed markup before it is inserted. */
function carryCartDrawerState(currentRoot, parsedRoot) {
  if (!currentRoot || !parsedRoot) return;
  const oldTimer = currentRoot.querySelector('countdown-timer');
  const newTimer = parsedRoot.querySelector('countdown-timer');
  if (oldTimer && newTimer && oldTimer.dataset.duration) newTimer.dataset.duration = oldTimer.dataset.duration;
}

/* Progress bars: remember widths before a re-render, then transition from old to new width. */
function snapshotProgressBars(root) {
  return Array.from(root?.querySelectorAll('.cart-progress__bar__progress') || [], (el) => el.style.width);
}

function animateProgressBars(root, previousWidths) {
  if (!root || !previousWidths.length) return;
  root.querySelectorAll('.cart-progress__bar__progress').forEach((bar, index) => {
    const from = previousWidths[index];
    const to = bar.style.width;
    if (from === undefined || from === to) return;
    bar.style.transition = 'none';
    bar.style.width = from;
    void bar.offsetWidth; // reflow so the transition starts from the old width
    bar.style.transition = '';
    bar.style.width = to;
  });
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.addEventListener('click', (evt) => {
      if (evt.target.id === 'CartDrawer-Overlay' || evt.target.closest('[data-cart-drawer-close]')) this.close();
    });
    this.setHeaderCartIconAccessibility();
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
    if (this.classList.contains('active')) this.playTimer();
  }

  disconnectedCallback() {
    if (this.cartUpdateUnsubscriber) this.cartUpdateUnsubscriber();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.playTimer();
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // The slide-in transition doesn't always run without a tick.
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  playTimer() {
    this.querySelector('countdown-timer')?.play?.();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  /* Called by product-form.js after /cart/add.js with the sections from getSectionsToRender(). */
  renderContents(parsedState, { open = true } = {}) {
    this.productId = parsedState.id;
    const progress = snapshotProgressBars(this);

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      const html = parsedState.sections?.[section.id];
      if (!sectionElement || !html) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(html, section.selector);
    });

    this.classList.toggle('is-empty', !!this.querySelector('.drawer__inner-empty'));
    animateProgressBars(this, progress);
    this.playTimer();

    if (open) setTimeout(() => this.open());
  }

  /* Re-render from a Section Rendering API map ({ 'cart-drawer': html, 'cart-icon-bubble': html }). */
  renderSections(sections) {
    this.renderContents({ sections }, { open: false });
  }

  /* Cart changed outside the drawer (cart page, quick add, Shopify standard actions…). */
  onCartUpdate(event = {}) {
    if (event.source === 'product-form') return; // product-form.js calls renderContents() itself
    if (event.source === 'cart-drawer') return;
    if (event.source === 'cart-items' && event.fromDrawer) return; // already re-rendered by CartDrawerItems

    return fetch(`${routes.cart_url}?sections=${CART_DRAWER_SECTION},cart-icon-bubble`)
      .then((response) => response.json())
      .then((sections) => {
        if (sections[CART_DRAWER_SECTION]) this.renderSections(sections);
      })
      .catch((e) => console.error(e));
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!parsed) return '';
    carryCartDrawerState(this, parsed);
    return parsed.innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: CART_DRAWER_SECTION,
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: CART_DRAWER_SECTION,
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  /* External cart changes are handled by <cart-drawer>, which refreshes all of .drawer__inner. */
  onCartUpdate() {
    return Promise.resolve();
  }

  getSectionInnerHTML(html, selector) {
    const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
    if (!parsed) return '';
    const drawer = document.querySelector('cart-drawer');
    if (selector === '.drawer__inner' && drawer) {
      carryCartDrawerState(drawer, parsed);
      const progress = snapshotProgressBars(drawer);
      requestAnimationFrame(() => {
        animateProgressBars(drawer, progress);
        drawer.playTimer();
      });
    }
    return parsed.innerHTML;
  }
}

if (!customElements.get('cart-drawer-items')) customElements.define('cart-drawer-items', CartDrawerItems);

/* Shopify money_format rendering for upsell variant prices. */
function brFormatMoney(cents, format) {
  const value = Math.max(0, Math.round(cents));
  const fmt = (decimals, thousands = ',', decimal = '.') => {
    const [int, frac] = (value / 100).toFixed(decimals).split('.');
    return int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands) + (frac ? decimal + frac : '');
  };
  return (format || '${{amount}}').replace(/\{\{\s*(\w+)\s*\}\}/, (_, token) => {
    switch (token) {
      case 'amount_no_decimals':
        return fmt(0);
      case 'amount_with_comma_separator':
        return fmt(2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fmt(0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return fmt(2, "'", '.');
      case 'amount_with_space_separator':
        return fmt(2, ' ', ',');
      default:
        return fmt(2);
    }
  });
}

/* <cart-drawer-upsell>: variant picker for an upsell. Adding is done by its nested Dawn <product-form>,
   which calls <cart-drawer>.renderContents() so the drawer refreshes. */
if (!customElements.get('cart-drawer-upsell')) {
  customElements.define(
    'cart-drawer-upsell',
    class CartDrawerUpsell extends HTMLElement {
      connectedCallback() {
        this.picker = this.querySelector('.upsell__variant-picker');
        if (!this.picker || this.bound) return;
        this.bound = true;
        this.picker.addEventListener('change', () => this.onVariantChange());
      }

      get variants() {
        if (!this._variants) {
          this._variants = JSON.parse(this.picker.querySelector('script[type="application/json"]').textContent);
        }
        return this._variants;
      }

      onVariantChange() {
        const selected = Array.from(this.picker.querySelectorAll('select'), (select) => select.value);
        const variant = this.variants.find((v) => v.options.every((option, i) => option === selected[i]));
        const button = this.querySelector('.upsell__add-btn');
        if (!variant) {
          button?.setAttribute('disabled', '');
          return;
        }

        this.querySelector('input[name="id"]').value = variant.id;
        button?.toggleAttribute('disabled', !variant.available);

        const price =
          variant.price * parseFloat(this.dataset.percentageLeft || 1) - parseFloat(this.dataset.fixedDiscount || 0);
        const compare = variant.compare_at_price > variant.price ? variant.compare_at_price : variant.price;
        const format = this.closest('[data-money-format]')?.dataset.moneyFormat;
        this.querySelectorAll('.upsell__price').forEach((el) => {
          const regular = el.querySelector('.regular-price');
          const compareEl = el.querySelector('.compare-price');
          if (regular) regular.textContent = brFormatMoney(price, format);
          if (compareEl) {
            compareEl.textContent = brFormatMoney(compare, format);
            compareEl.classList.toggle('hidden', !(compare > price));
          }
        });

        const image = this.querySelector('.upsell__image__img');
        if (image && this.dataset.updateImage === 'true' && variant.featured_image) {
          image.removeAttribute('srcset');
          image.src = variant.featured_image.src;
        }
      }
    }
  );
}

/* <cart-discount>: apply / remove discount codes with the Ajax Cart API and re-render the drawer. */
if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      connectedCallback() {
        if (this.bound) return;
        this.bound = true;
        this.form = this.querySelector('form');
        this.input = this.querySelector('input[name="cart-discount-field"]');
        this.error = this.querySelector('.cart-discount-form__error');
        this.form.addEventListener('submit', (event) => this.onSubmit(event));
        this.input.addEventListener('input', () => this.showError(''));
        this.addEventListener('click', (event) => {
          const remove = event.target.closest('.cart-discount__remove');
          if (remove) this.removeCode(remove.closest('[data-code]').dataset.code);
        });
      }

      get appliedCodes() {
        return Array.from(this.querySelectorAll('.cart-discount__code[data-code]'), (el) => el.dataset.code);
      }

      showError(message) {
        this.error.textContent = message;
        this.error.hidden = !message;
      }

      setLoading(loading) {
        const button = this.form.querySelector('button[type="submit"]');
        button.classList.toggle('loading', loading);
        button.toggleAttribute('disabled', loading);
        this.form.querySelector('.loading__spinner')?.classList.toggle('hidden', !loading);
      }

      onSubmit(event) {
        event.preventDefault();
        const code = this.input.value.trim();
        if (!code) {
          this.showError(this.dataset.emptyMsg);
          return;
        }
        const previous = this.appliedCodes;
        if (previous.some((c) => c.toLowerCase() === code.toLowerCase())) {
          this.input.value = '';
          return;
        }

        this.setLoading(true);
        this.update([...previous, code])
          .then((cart) => {
            const result = cart.discount_codes?.find((d) => d.code.toLowerCase() === code.toLowerCase());
            if (!result || result.applicable) return { cart, invalid: false };
            // Not applicable: restore the previous codes so the cart doesn't keep a dead code.
            return this.update(previous).then((restored) => ({ cart: restored, invalid: true }));
          })
          .then(({ cart, invalid }) => {
            this.render(cart);
            if (!invalid) return;
            const fresh = document.querySelector('cart-discount');
            if (!fresh) return;
            fresh.input.value = code;
            fresh.showError(fresh.dataset.invalidMsg);
          })
          .catch(() => {
            this.setLoading(false);
            this.showError(window.cartStrings?.error || this.dataset.invalidMsg);
          });
      }

      removeCode(code) {
        this.update(this.appliedCodes.filter((c) => c !== code))
          .then((cart) => this.render(cart))
          .catch((e) => console.error(e));
      }

      update(codes) {
        const body = JSON.stringify({
          discount: codes.join(','),
          sections: [CART_DRAWER_SECTION, 'cart-icon-bubble'],
          sections_url: window.location.pathname,
        });
        return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), body }).then((response) => {
          if (!response.ok) throw new Error(String(response.status));
          return response.json();
        });
      }

      render(cart) {
        const drawer = this.closest('cart-drawer');
        if (drawer && cart.sections) drawer.renderSections(cart.sections);
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: cart });
      }
    }
  );
}

/* Theme editor: open the drawer while its section or blocks are selected. */
if (window.Shopify && Shopify.designMode) {
  const isDrawerEvent = (event) => event.detail?.sectionId === CART_DRAWER_SECTION;
  ['shopify:section:select', 'shopify:block:select'].forEach((name) =>
    document.addEventListener(name, (event) => {
      if (isDrawerEvent(event)) document.querySelector('cart-drawer')?.open();
    })
  );
  document.addEventListener('shopify:section:deselect', (event) => {
    if (isDrawerEvent(event)) document.querySelector('cart-drawer')?.close();
  });
}
